import { useEffect, useState, useCallback } from "react";
import { useWorkforce } from "../hooks/useWorkforce";
import { useModals } from "../../../context/ModalContext";
import { WorkforceCreate } from "../components/WorkforceCreate";
import { Box } from "@mui/material";
import { GenericProcessLoader } from "../../shared/components/loaders/GenericProcessLoader";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { useAppSelector } from "../../../redux/hooks/redux";
import { selectEntitlement } from "../../../permissions/benefits/redux/benefits.selectors";
import { EBenefitTypes } from "../../../permissions/benefits/enums/benefits.enums";

export const WorkforceCreateView = ({ defaultValues = undefined, modalKey = "Dashboard-Workforce-Create", onSuccess = null } = {}) => {
  const { closeByKey } = useModals();
  const { workforceMutation } = useWorkforce();
  const { isPending, isSuccess } = workforceMutation;
  const { showSnackbar } = useGlobalSnackbar();

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (isPending) {
      setShowLoader(true);
    }
  }, [isPending]);

  const handleLoaderFinish = useCallback(() => {
    setShowLoader(false);
    workforceMutation.reset?.();
    closeByKey(modalKey);
    onSuccess?.();
  }, [workforceMutation, closeByKey, modalKey, onSuccess]);

  const numberOfSeats = useAppSelector(selectEntitlement(EBenefitTypes.SEATS));
  const remaining = numberOfSeats ? Math.max(0, (numberOfSeats.limit ?? 0) - (numberOfSeats.used ?? 0)) : 0;

  useEffect(() => {
    if (numberOfSeats === undefined) return;
    showSnackbar(
      `This action will consume one workforce seat. You have ${remaining} seat${remaining !== 1 ? "s" : ""} left.`,
      "info",
      4000,
      "bottom-left",
    );
  }, [numberOfSeats]);

  const isApplicant = defaultValues?.status === "APPLICANT";

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
      <WorkforceCreate defaultValues={defaultValues} workforceMutation={workforceMutation} />

      {showLoader && (
        <GenericProcessLoader
          isPending={isPending}
          isSuccess={isSuccess}
          delayMs={3000}
          status={{
            first: isApplicant ? "Generating applicant profile" : "Generating employee profile",
            second: isApplicant ? "Registering applicant information" : "Registering coverage area",
            third: "Securing system registration",
          }}
          onFinish={handleLoaderFinish}
        />
      )}
    </Box>
  );
};
